import { rem } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Skeleton } from '@chakra-ui/react/skeleton';
import { useEffect, useMemo, useState } from 'react';

import { BuildDetailsTabId } from '@/enums/BuildDetailsTabs';
import { segmentTrack } from '@/hooks/analytics/useTracking';
import useStepLib from '@/hooks/api/useStepLib';
import useQueryParams from '@/hooks/useQueryParams';
import { isBuildNotFinished, isBuildNotStarted } from '@/models/Build';
import useBuildDetailsPageStore, { updateLogViewVersion } from '@/pages/BuildDetailsPage/BuildDetailsPage.store';
import { getStepsData, GetStepsDataReturn } from '@/utils/buildLog';

import BuildLogEmptyState from '../BuildLogEmptyState/BuildLogEmptyState';
import BuildEnvironmentLog from './BuildEnvironmentLog';
import { StepList } from './StepList';
import StructuredBuildLogContext from './StructuredBuildLogContext';

export const StructuredBuildLog = () => {
  const queryParams = useQueryParams();
  const tabParam = queryParams.get('tab');
  const stepParam = queryParams.get('step');

  const { app, abilities, build, isViewedAsPublicPage, trackingId, logSummary, logSummaryStatus } =
    useBuildDetailsPageStore();

  const [stepsData, setStepsData] = useState<GetStepsDataReturn | null>(null);
  const { data, isLoading: isStepLibLoading } = useStepLib();

  useEffect(() => {
    if (data && !isStepLibLoading) {
      setStepsData(getStepsData(data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStepLibLoading]);

  useEffect(() => {
    if (tabParam === BuildDetailsTabId.log) {
      segmentTrack(`Build Details Structured Log Displayed`, {
        event_type: 'technical',
        page_load_id: trackingId,
        app_slug: app.slug,
        build_slug: build.slug,
      });

      updateLogViewVersion('structured');
    }
  }, [app.slug, build.slug, tabParam, trackingId]);

  useEffect(() => {
    if (stepParam && stepsData) {
      setTimeout(() => {
        const stepNode = document.getElementById(stepParam);
        stepNode?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [stepParam, stepsData]);

  const structuredBuildLogContextValue = useMemo(() => {
    if (stepsData) {
      return { execution: logSummary?.execution, stepsData, stepToOpen: stepParam };
    }
    return { stepToOpen: stepParam };
  }, [stepParam, stepsData, logSummary?.execution]);

  if (logSummaryStatus === 'fetching' || isStepLibLoading) {
    return (
      <Box>
        <Skeleton height="24" marginBlockEnd="16" width={rem(160)} />
        <Skeleton height={rem(58)} />
      </Box>
    );
  }

  if (isBuildNotStarted(build) && isBuildNotFinished(build)) {
    return <BuildLogEmptyState state="WaitingToBeStarted" />;
  }

  if (logSummaryStatus === 'deleted') {
    return <BuildLogEmptyState state="LogsDeleted" />;
  }

  if (logSummaryStatus === 'error' || build.triggeredWorkflow === null) {
    return <BuildLogEmptyState state="LoadingError" />;
  }

  return (
    <StructuredBuildLogContext.Provider value={structuredBuildLogContextValue}>
      {logSummary?.hasBuildEnvironmentSetupLogs && !!logSummary.execution?.buildEnvironmentSetup && (
        <BuildEnvironmentLog
          buildEnvironmentSetup={logSummary.execution?.buildEnvironmentSetup}
          projectSlug={app.slug}
          buildSlug={build.slug}
          viewedAsPublicPage={isViewedAsPublicPage}
        />
      )}
      <StepList
        appSlug={app.slug}
        buildSlug={build.slug}
        triggeredWorkflow={build.triggeredWorkflow}
        canChangeSettings={abilities.canChangeSettings}
        trackingId={trackingId}
        viewedAsPublicPage={isViewedAsPublicPage}
        buildLogSummary={logSummary}
      />
    </StructuredBuildLogContext.Provider>
  );
};
